import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Tooltip,
  Avatar,
  Chip,
  useTheme
} from '@mui/material';
import {
  EmojiEvents as EmojiEventsIcon,
  Star as StarIcon,
  Whatshot as WhatshotIcon,
  School as SchoolIcon,
  BrightnessAuto as BrightnessAutoIcon,
  Forum as ForumIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const BadgeCard = ({ badge, compact = false }) => {
  const theme = useTheme();
  const { t } = useTranslation(['badges', 'common']);
  
  // Функция для получения цвета в зависимости от редкости значка
  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common':
        return '#989898';
      case 'uncommon':
        return '#58ac30';
      case 'rare':
        return '#3d85c6';
      case 'epic':
        return '#9b59b6';
      case 'legendary':
        return '#e67e22';
      default:
        return '#989898';
    }
  };
  
  // Функция для получения цвета тира значка
  const getTierColor = (tier) => {
    switch (tier) {
      case 'bronze':
        return '#cd7f32';
      case 'silver':
        return '#c0c0c0';
      case 'gold':
        return '#ffd700';
      case 'platinum':
        return '#e5e4e2';
      default:
        return '#cd7f32';
    }
  };
  
  // Функция для получения иконки значка
  const getBadgeIcon = (category) => {
    switch (category) {
      case 'course':
        return <SchoolIcon />;
      case 'achievement':
        return <EmojiEventsIcon />;
      case 'community':
        return <ForumIcon />;
      case 'special':
        return <StarIcon />;
      case 'seasonal':
        return <BrightnessAutoIcon />;
      case 'challenge':
        return <WhatshotIcon />;
      case 'streak':
        return <WhatshotIcon />;
      case 'skill':
        return <CodeIcon />;
      default:
        return <StarIcon />;
    }
  };
  
  // Если значок не существует, возвращаем null
  if (!badge) return null;
  
  // Если компактный режим, возвращаем миниатюру значка
  if (compact) {
    return (
      <Tooltip title={`${badge.name} (${t(`badges:rarity.${badge.rarity}`)})`} arrow>
        <Box
          component={Avatar}
          src={badge.image}
          alt={badge.name}
          sx={{
            width: 40,
            height: 40,
            border: `2px solid ${getRarityColor(badge.rarity)}`,
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: `0 0 5px ${getRarityColor(badge.rarity)}`
            },
            transition: 'transform 0.3s ease, box-shadow 0.3s ease'
          }}
        >
          {getBadgeIcon(badge.category)}
        </Box>
      </Tooltip>
    );
  }
  
  return (
    <Card
      elevation={1}
      sx={{
        borderRadius: 2,
        overflow: 'visible',
        position: 'relative',
        height: '100%',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: `0 8px 15px rgba(0, 0, 0, 0.1), 0 0 5px ${getRarityColor(badge.rarity)}`,
        },
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Иконка категории значка */}
      <Box
        sx={{
          position: 'absolute',
          top: -10,
          right: -10,
          backgroundColor: getRarityColor(badge.rarity),
          borderRadius: '50%',
          width: 35,
          height: 35,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          zIndex: 1,
          boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)'
        }}
      >
        {getBadgeIcon(badge.category)}
      </Box>
      
      {/* Изображение значка */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          height: 120,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(45deg, #2d3748 0%, #1a202c 100%)' 
            : 'linear-gradient(45deg, #f6f6f6 0%, #e9e9e9 100%)'
        }}
      >
        <Avatar
          src={badge.image}
          alt={badge.name}
          sx={{
            width: 80,
            height: 80,
            boxShadow: `0 0 10px ${getRarityColor(badge.rarity)}`,
            border: `3px solid ${getTierColor(badge.tier)}`
          }}
        />
      </Box>
      
      <CardContent sx={{ pb: '16px !important' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" noWrap title={badge.name}>
            {badge.name}
          </Typography>
          <Chip 
            label={t(`badges:tier.${badge.tier}`)} 
            size="small" 
            sx={{ 
              height: 20, 
              fontSize: '0.7rem', 
              bgcolor: getTierColor(badge.tier), 
              color: badge.tier === 'platinum' ? '#000' : '#fff' 
            }} 
          />
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ height: 60, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {badge.description}
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Chip 
            label={t(`badges:category.${badge.category}`)} 
            size="small" 
            sx={{ height: 20, fontSize: '0.7rem' }} 
          />
          <Chip 
            label={t(`badges:rarity.${badge.rarity}`)} 
            size="small" 
            sx={{ 
              height: 20, 
              fontSize: '0.7rem', 
              bgcolor: getRarityColor(badge.rarity), 
              color: '#fff' 
            }} 
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default BadgeCard; 